<template>
  <div
    ref="modalEl"
    class="modal fade"
    tabindex="-1"
    aria-labelledby="stockShipmentTitle"
    aria-hidden="true"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="stockShipmentTitle">Versand an Servicepartner</h5>
          <button
            type="button"
            class="btn-close"
            :disabled="saving"
            data-bs-dismiss="modal"
            aria-label="Schließen"
          ></button>
        </div>

        <div class="modal-body">
          <!-- Druckerauswahl aus dem Solo, ganz oben — das Etikett geht auf den
               Post-Etikettendrucker dieser Station. Die Höhe meldet das Widget
               selbst per postMessage, sonst wird das Dropdown abgeschnitten. -->
          <div v-if="printerEmbedUrl" class="printer-frame-wrap">
            <iframe
              class="printer-frame"
              title="Druckerauswahl"
              :src="printerEmbedUrl"
              :style="{ height: printerFrameHeight + 'px' }"
            ></iframe>
          </div>
          <div v-else-if="printerError" class="error">{{ printerError }}</div>
          <div v-else class="spinner" aria-label="Lädt"></div>

          <dl v-if="device" class="shipment-device">
            <div class="shipment-field">
              <dt>R2-Nummer</dt>
              <dd class="shipment-number">{{ device.stock_number }}</dd>
            </div>
            <div class="shipment-field">
              <dt>IMEI / SN</dt>
              <dd class="shipment-mono">{{ device.imei_sn || '—' }}</dd>
            </div>
            <div class="shipment-field">
              <dt>Gerät</dt>
              <dd>
                {{ device.master.description }}
                <span v-if="device.is_legacy_device" class="badge bg-secondary">Altgerät</span>
              </dd>
            </div>
            <template v-if="!device.is_legacy_device">
            <div class="shipment-field">
              <dt>Farbe</dt>
              <dd>{{ device.color?.description ?? '—' }}</dd>
            </div>
            <div class="shipment-field">
              <dt>Speicher</dt>
              <dd>{{ device.memory_size_label }}</dd>
            </div>
            </template>
            <div class="shipment-field">
              <dt>Zustand</dt>
              <dd>{{ device.condition ? device.condition.description : '—' }}</dd>
            </div>
          </dl>

          <div v-if="optionsLoading" class="spinner" aria-label="Lädt"></div>
          <div v-else-if="optionsError" class="error">{{ optionsError }}</div>
          <template v-else>
            <!-- Steht über allem: das Hakerl entscheidet, woher die Partnerliste
                 kommt und ob ein Fehlerbild oder ein Text verlangt wird. Auch bei
                 fehlender Rezeptur erreichbar — genau dann braucht man es. -->
            <div class="form-check mb-3">
              <input
                id="shipment-warranty"
                v-model="isWarranty"
                class="form-check-input"
                type="checkbox"
                :disabled="saving"
              />
              <label class="form-check-label" for="shipment-warranty">
                Garantie — ohne Fehlerbild und ohne Preis
              </label>
            </div>

            <template v-if="isWarranty">
              <div class="mb-3">
                <label for="shipment-warranty-partner" class="form-label">Servicepartner</label>
                <select
                  id="shipment-warranty-partner"
                  v-model="partnerId"
                  class="form-select"
                  :disabled="saving"
                >
                  <option :value="null" disabled>Bitte wählen …</option>
                  <option v-for="p in warrantyPartners" :key="p.id" :value="p.id">{{ p.description }}</option>
                </select>
              </div>

              <!-- Nur, wenn die Wahl vom Master abweicht: sonst gäbe es nichts zu
                   speichern. Beim leeren Master ist es vorausgewählt. -->
              <div v-if="showSaveAsMasterDefault && warranty.can_save_as_default" class="form-check mb-3">
                <input
                  id="shipment-warranty-default"
                  v-model="saveAsMasterDefault"
                  class="form-check-input"
                  type="checkbox"
                  :disabled="saving"
                />
                <label class="form-check-label" for="shipment-warranty-default">
                  als Standard — diesen Garantiepartner am Masterartikel hinterlegen
                </label>
              </div>

              <div class="mb-3">
                <label for="shipment-damage" class="form-label">Fehlerbeschreibung</label>
                <textarea
                  id="shipment-damage"
                  v-model="damageDescription"
                  class="form-control"
                  rows="4"
                  :maxlength="DAMAGE_DESCRIPTION_MAX_LENGTH"
                  :disabled="saving"
                  placeholder="Was ist am Gerät kaputt?"
                ></textarea>
                <div class="form-text damage-counter">
                  Kommt auf die zweite Seite des Versandetiketts.
                  {{ damageDescription.trim().length }} / {{ DAMAGE_DESCRIPTION_MAX_LENGTH }}
                </div>
              </div>
            </template>

            <!-- Ohne Rezeptur gibt es keinen Rezepturversand — dann bleibt nur der
                 Hinweis, statt einer leeren Auswahl. -->
            <div v-else-if="options.length === 0" class="error">
              Für dieses Gerät ist keine Rezeptur hinterlegt (Master × Servicepartner × Fehlerbild).
              Ein Versand ist erst nach dem Anlegen der Rezeptur möglich — oder auf Garantie.
            </div>

            <template v-else>
              <div class="mb-3">
                <label for="shipment-partner" class="form-label">Servicepartner</label>
                <select
                  id="shipment-partner"
                  v-model="partnerId"
                  class="form-select"
                  :disabled="saving"
                >
                  <option :value="null" disabled>Bitte wählen …</option>
                  <option v-for="p in options" :key="p.id" :value="p.id">{{ p.description }}</option>
                </select>
              </div>

              <div class="mb-3">
                <label for="shipment-pattern" class="form-label">Fehlerbild</label>
                <select
                  id="shipment-pattern"
                  v-model="errorPatternId"
                  class="form-select"
                  :disabled="saving || partnerId === null"
                >
                  <option :value="null" disabled>
                    {{ partnerId === null ? 'Zuerst Servicepartner wählen' : 'Bitte wählen …' }}
                  </option>
                  <!-- Preis = Aufschlag der Rezeptur; steht so auch auf dem Etikett. -->
                  <option v-for="pattern in patterns" :key="pattern.id" :value="pattern.id">
                    {{ pattern.description }}{{ pattern.price_label ? ` · ${pattern.price_label}` : '' }}
                  </option>
                </select>
              </div>

              <!-- Altgerät: keine Rezeptur, also kein Preis daraus — Pflichtfeld,
                   steht so auf Etikett und Lieferschein. -->
              <div v-if="manualPriceRequired" class="mb-3">
                <label for="shipment-manual-price" class="form-label">Preis netto (€)</label>
                <input
                  id="shipment-manual-price"
                  v-model="manualPriceText"
                  type="text"
                  inputmode="decimal"
                  class="form-control"
                  :class="{ 'is-invalid': manualPriceInvalid }"
                  placeholder="z.B. 45,00"
                  :disabled="saving || errorPatternId === null"
                />
                <div v-if="manualPriceInvalid" class="invalid-feedback">
                  Bitte einen Netto-Preis zwischen 0,01 € und 9.999,99 € angeben.
                </div>
              </div>
            </template>
          </template>

          <!-- Interner Servicepartner: Lieferschein und Übernahme gleich mit, ohne
               Abscannen. Gilt für jede Versandart, hängt nur am Partner. -->
          <div v-if="partnerIsInstant && !optionsLoading && !optionsError" class="form-check mb-3">
            <input
              id="shipment-instant-closing"
              v-model="instantSlipClosing"
              class="form-check-input"
              type="checkbox"
              :disabled="saving"
            />
            <label class="form-check-label" for="shipment-instant-closing">
              Sofortiger LS-Abschluss — Lieferschein anlegen und gleich beim Servicepartner übernehmen
            </label>
          </div>

          <div v-if="saving" class="print-status">
            <div class="spinner" aria-label="Druckt"></div>
            <span>Etikett wird gedruckt …</span>
          </div>
          <div v-else-if="errorMessage" class="error print-error">{{ errorMessage }}</div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" :disabled="saving" data-bs-dismiss="modal">
            Abbrechen
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="saving || !canSave"
            @click="onSave"
          >{{ saving ? 'Speichert …' : 'Speichern' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue';
import { Modal } from 'bootstrap';
import {
  createServicepartnerShipment,
  listServicepartnerOptions,
  DAMAGE_DESCRIPTION_MAX_LENGTH,
  parseManualPrice,
  type ServicepartnerOption,
  type StockDevice,
  type WarrantyServicepartnerOption,
  type WarrantyShipmentOptions,
} from '../services/stockDevices';
import { getPeppiPrinterUrls } from '../services/me';

/**
 * Versand eines einzelnen Lagergeräts an einen Servicepartner.
 *
 * Auswählbar sind nur Kombinationen, für die es zum Master eine Rezeptur gibt —
 * die Liste kommt fertig vom Server, das Fehlerbild hängt am Servicepartner.
 *
 * Mit dem Hakerl „Garantie" wechselt der untere Teil: Partner aus allen aktiven
 * Servicepartnern (vorausgewählt der am Master hinterlegte), kein Fehlerbild,
 * dafür eine Pflicht-Fehlerbeschreibung, vorbefüllt aus dem Ticket. Beide Listen
 * kommen aus demselben Request, das Umlegen lädt also nichts nach.
 *
 * „Speichern" druckt das Etikett auf dem Post-Etikettendrucker der gewählten
 * Station und legt die Versandzeile an. Druckt die Station nicht, ist der Versand
 * trotzdem angelegt — der Druckauftrag liegt in der Warteschlange und ist dort
 * nachdruckbar. Das Modal schließt dann ebenfalls; die Meldung holt der Aufrufer
 * über `lastPrintWarning()` und zeigt sie an.
 *
 * Offen bleibt das Modal nur, wenn nichts gespeichert wurde (kein Drucker
 * hinterlegt, keine Rezeptur zur Kombination).
 *
 * Altgerät ohne Master: statt der Rezepturkombinationen stehen alle aktiven
 * Partner und Fehlerbilder zur Wahl (`manual_price_required`), dazu ein
 * Pflichtfeld für den Netto-Preis.
 *
 * Interner Servicepartner (`instant_slip_closing`): das Häkchen „Sofortiger
 * LS-Abschluss" erscheint und ist bei jeder Partnerwahl gesetzt. Der Server legt
 * dann gleich Lieferschein und Übernahme an; scheitert davon etwas, holt der
 * Aufrufer die Meldung über `lastInstantClosingWarning()`.
 *
 * `open(device)` liefert true, wenn der Versand angelegt wurde.
 */
const modalEl = useTemplateRef<HTMLDivElement>('modalEl');
let   modal: Modal | null = null;
let   resolver: ((saved: boolean) => void) | null = null;
let   saved = false;
let   printWarning: string | null = null;
let   instantClosingWarning: string | null = null;

// Wie im Druckmodal: ein hide() während der Show-Transition verwirft Bootstrap.
let isShown  = false;
let wantHide = false;

const EMPTY_WARRANTY: WarrantyShipmentOptions = {
  servicepartners:                    [],
  default_servicepartner_location_id: null,
  damage_description:                 '',
  can_save_as_default:                true,
};

const device         = ref<StockDevice | null>(null);
const options        = ref<ServicepartnerOption[]>([]);
const warranty       = ref<WarrantyShipmentOptions>(EMPTY_WARRANTY);
const optionsLoading = ref(false);
const optionsError   = ref<string | null>(null);
const partnerId      = ref<number | null>(null);
const errorPatternId = ref<number | null>(null);
const isWarranty          = ref(false);
const damageDescription   = ref('');
const saveAsMasterDefault = ref(false);
// Altgerät: Partner und Fehlerbild frei aus den Stammdaten, Preis händisch.
const manualPriceRequired = ref(false);
const manualPriceText     = ref('');
const manualPrice         = computed<number | null>(() => parseManualPrice(manualPriceText.value));
const manualPriceInvalid  = computed<boolean>(
  () => manualPriceRequired.value && manualPriceText.value.trim() !== '' && manualPrice.value === null,
);
const instantSlipClosing  = ref(true);
const saving         = ref(false);
const errorMessage   = ref<string | null>(null);

const printerEmbedUrl    = ref<string | null>(null);
const printerError       = ref<string | null>(null);
const printerFrameHeight = ref(60);

const patterns         = computed(() => options.value.find(p => p.id === partnerId.value)?.error_patterns ?? []);
const warrantyPartners = computed<WarrantyServicepartnerOption[]>(() => warranty.value.servicepartners);

// Das Kennzeichen kommt aus der Liste, aus der gerade gewählt wird.
const partnerIsInstant = computed<boolean>(() => {
  if (partnerId.value === null) return false;
  const partner = isWarranty.value
    ? warrantyPartners.value.find(p => p.id === partnerId.value)
    : options.value.find(p => p.id === partnerId.value);
  return partner?.instant_slip_closing === true;
});

const canSave = computed(() => {
  if (partnerId.value === null) return false;
  if (isWarranty.value) return damageDescription.value.trim().length > 0;
  if (manualPriceRequired.value && manualPrice.value === null) return false;
  return errorPatternId.value !== null;
});

// „als Standard" nur, wenn es etwas zu speichern gibt — steht am Master schon
// derselbe Partner, wäre das Hakerl folgenlos.
const showSaveAsMasterDefault = computed<boolean>(() =>
  isWarranty.value
  && partnerId.value !== null
  && partnerId.value !== warranty.value.default_servicepartner_location_id);

// Partnerwechsel wirft das Fehlerbild weg: es gilt immer nur für einen Partner.
// Der Sofortabschluss ist bei jeder neuen Wahl wieder gesetzt (Standard).
watch(partnerId, () => {
  errorPatternId.value     = null;
  instantSlipClosing.value = true;
});

// Umschalten wechselt die Partnerquelle — die alte Auswahl gilt in der neuen Liste
// nicht. Auf Garantie steht der Master-Partner vorausgewählt und der Ticket-Text im
// Feld; ist am Master noch keiner hinterlegt, ist „als Standard" gleich angehakt.
watch(isWarranty, on => {
  errorPatternId.value = null;
  if (on) {
    partnerId.value           = warranty.value.default_servicepartner_location_id;
    damageDescription.value   = warranty.value.damage_description;
    saveAsMasterDefault.value = warranty.value.default_servicepartner_location_id === null;
    return;
  }
  partnerId.value           = options.value.length === 1 ? options.value[0].id : null;
  saveAsMasterDefault.value = false;
});

onMounted(() => {
  window.addEventListener('message', onPrinterFrameMessage);
  if (modalEl.value) {
    modal = new Modal(modalEl.value);
    modalEl.value.addEventListener('shown.bs.modal', onShown);
    modalEl.value.addEventListener('hidden.bs.modal', onHidden);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('message', onPrinterFrameMessage);
  modalEl.value?.removeEventListener('shown.bs.modal', onShown);
  modalEl.value?.removeEventListener('hidden.bs.modal', onHidden);
  modal?.dispose();
  modal = null;
});

function onPrinterFrameMessage(event: MessageEvent): void {
  if (printerEmbedUrl.value === null) return;
  if (event.origin !== new URL(printerEmbedUrl.value).origin) return;
  const { type, height } = event.data ?? {};
  if (type === 'PEPPI_PRINTER_HEIGHT' && typeof height === 'number') {
    printerFrameHeight.value = Math.max(40, Math.min(height, 500));
  }
}

async function open(selected: StockDevice): Promise<boolean> {
  device.value              = selected;
  options.value             = [];
  warranty.value            = EMPTY_WARRANTY;
  optionsError.value        = null;
  partnerId.value           = null;
  errorPatternId.value      = null;
  isWarranty.value          = false;
  damageDescription.value   = '';
  saveAsMasterDefault.value = false;
  manualPriceRequired.value = false;
  manualPriceText.value     = '';
  instantSlipClosing.value  = true;
  errorMessage.value        = null;
  saving.value         = false;
  saved                = false;
  printWarning         = null;
  instantClosingWarning = null;
  isShown              = false;
  wantHide             = false;
  modal?.show();
  void loadPrinterUrl();
  void loadOptions(selected.id);
  return new Promise<boolean>(resolve => {
    resolver = resolve;
  });
}

async function loadPrinterUrl(): Promise<void> {
  if (printerEmbedUrl.value !== null) return;
  printerError.value = null;
  try {
    printerEmbedUrl.value = (await getPeppiPrinterUrls()).embedUrl;
  } catch (e) {
    printerError.value = e instanceof Error
      ? `Druckerauswahl konnte nicht geladen werden: ${e.message}`
      : 'Druckerauswahl konnte nicht geladen werden.';
  }
}

async function loadOptions(deviceId: number): Promise<void> {
  optionsLoading.value = true;
  optionsError.value   = null;
  try {
    const loaded    = await listServicepartnerOptions(deviceId);
    options.value   = loaded.recipes;
    warranty.value  = loaded.warranty;
    manualPriceRequired.value = loaded.manual_price_required;
    // Genau ein Partner: Vorauswahl, damit nur noch das Fehlerbild fehlt.
    if (options.value.length === 1) partnerId.value = options.value[0].id;
  } catch (e) {
    optionsError.value = e instanceof Error
      ? `Servicepartner konnten nicht geladen werden: ${e.message}`
      : 'Servicepartner konnten nicht geladen werden.';
  } finally {
    optionsLoading.value = false;
  }
}

async function onSave(): Promise<void> {
  if (saving.value || device.value === null || !canSave.value || partnerId.value === null) return;
  saving.value       = true;
  errorMessage.value = null;
  try {
    const result = await createServicepartnerShipment(device.value.id, isWarranty.value
      ? {
          servicepartner_location_id: partnerId.value,
          is_warranty:                true,
          damage_description:         damageDescription.value.trim(),
          save_as_master_default:     warranty.value.can_save_as_default
            && showSaveAsMasterDefault.value && saveAsMasterDefault.value,
          instant_slip_closing:       partnerIsInstant.value && instantSlipClosing.value,
        }
      : {
          servicepartner_location_id: partnerId.value,
          error_pattern_id:           errorPatternId.value,
          manual_price_net_eur:       manualPriceRequired.value ? manualPrice.value : null,
          instant_slip_closing:       partnerIsInstant.value && instantSlipClosing.value,
        });
    saved        = true;
    printWarning = result.printed ? null : result.print_warning;
    instantClosingWarning = result.instant_closing?.attempted && !result.instant_closing.completed
      ? result.instant_closing.message
      : null;
    requestHide();
  } catch (e) {
    errorMessage.value = e instanceof Error ? e.message : 'Der Versand ist fehlgeschlagen.';
  } finally {
    saving.value = false;
  }
}

function onShown(): void {
  isShown = true;
  if (wantHide) {
    wantHide = false;
    modal?.hide();
  }
}

function requestHide(): void {
  if (isShown) modal?.hide();
  else wantHide = true;
}

function onHidden(): void {
  isShown = false;
  resolver?.(saved);
  resolver = null;
}

/** Gesetzt, wenn der Versand angelegt, das Etikett aber nicht gedruckt wurde. */
function lastPrintWarning(): string | null {
  return printWarning;
}

/** Gesetzt, wenn der Versand steht, der Sofortige LS-Abschluss aber nicht durchlief. */
function lastInstantClosingWarning(): string | null {
  return instantClosingWarning;
}

defineExpose({ open, lastPrintWarning, lastInstantClosingWarning });
</script>

<style scoped>
.printer-frame-wrap {
  border: 1px solid var(--bs-border-color, #d4d4d8);
  border-radius: 8px;
  padding: 4px 10px;
}

.printer-frame {
  display: block;
  width: 100%;
  border: 0;
}

.shipment-device {
  margin: 16px 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 18px;
}

.shipment-field dt {
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--bs-secondary-color, #6c757d);
}

.shipment-field dd {
  margin: 0;
}

.shipment-number {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.shipment-mono {
  font-family: var(--bs-font-monospace, monospace);
}

.print-status {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 14px;
  font-size: 15px;
}

.print-error {
  margin-top: 14px;
}

.damage-counter {
  font-variant-numeric: tabular-nums;
}

@media (max-width: 575.98px) {
  .shipment-device {
    grid-template-columns: 1fr;
  }
}
</style>
